import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, CheckCircle2, History, Loader2, RotateCcw, Save } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { get, put } from "@/lib/api";
import { errorMessage } from "@/lib/errors";
import { formatDateTime } from "@/lib/format";
import { cn } from "@/lib/utils";
import { PageHeader } from "@/components/common/PageHeader";
import { EmptyState } from "@/components/common/EmptyState";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { SECTION_DEFS, FIELD_SECTION, SectionForm } from "@/pages/brand-brain/sections";

const SECTION_KEYS = SECTION_DEFS.map((s) => s.key);

export default function BrandBrainPage() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { orgId, hasRole } = useAuth();
  const [params, setParams] = useSearchParams();
  const initial = params.get("section");
  const [section, setSection] = useState(SECTION_KEYS.includes(initial) ? initial : "identity");
  const [edits, setEdits] = useState({});
  const canEdit = hasRole("member");

  const { data, isLoading, isError, refetch } = useQuery({ queryKey: ["brand-brain", orgId], queryFn: () => get("/brand-brain"), enabled: !!orgId });
  const { data: revisions } = useQuery({ queryKey: ["brand-brain-revisions", orgId], queryFn: () => get("/brand-brain/revisions?limit=20"), enabled: !!orgId });

  useEffect(() => {
    setEdits({});
  }, [orgId]);

  const draft = useMemo(() => ({ ...(data || {}), ...edits }), [data, edits]);
  const dirty = Object.keys(edits).length > 0;

  const setField = useCallback((key, value) => setEdits((e) => ({ ...e, [key]: value })), []);
  const setNested = useCallback((key, sub, value) => {
    setEdits((e) => {
      const base = e[key] !== undefined ? e[key] : (data?.[key] || {});
      return { ...e, [key]: { ...base, [sub]: value } };
    });
  }, [data]);

  const selectSection = (key) => {
    setSection(key);
    setParams({ section: key }, { replace: true });
  };

  const save = useMutation({
    mutationFn: () => put("/brand-brain", edits),
    onSuccess: (saved) => {
      queryClient.setQueryData(["brand-brain", orgId], saved);
      queryClient.invalidateQueries({ queryKey: ["brand-brain-revisions", orgId] });
      setEdits({});
      toast.success(t("brandBrain.saved"));
    },
    onError: (err) => toast.error(errorMessage(err)),
  });

  useEffect(() => {
    if (!dirty) return undefined;
    const handler = (e) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [dirty]);

  const readiness = data?.readiness;
  const sectionPct = readiness?.sections || {};
  const active = SECTION_DEFS.find((s) => s.key === section);

  if (isError) {
    return <EmptyState icon={AlertTriangle} title={t("common.error")} hint={t("errors.network_error")} action={<Button onClick={() => refetch()} data-testid="brand-brain-retry">{t("common.retry")}</Button>} testId="brand-brain-error" />;
  }

  return (
    <div className="space-y-6" data-testid="brand-brain-page">
      <PageHeader
        title={t("brandBrain.title")}
        subtitle={t("brandBrain.subtitle")}
        testId="brand-brain-header"
        actions={dirty && canEdit ? (
          <>
            <Button variant="ghost" onClick={() => setEdits({})} disabled={save.isPending} data-testid="brand-brain-discard-top"><RotateCcw className="mr-2 h-4 w-4" />{t("brandBrain.discard")}</Button>
            <Button onClick={() => save.mutate()} disabled={save.isPending} data-testid="brand-brain-save-top">{save.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}{t("common.save")}</Button>
          </>
        ) : null}
      />

      {/* Readiness */}
      <section className="og-card p-5" data-testid="brand-brain-readiness">
        {isLoading || !data ? (
          <div className="space-y-3"><Skeleton className="h-5 w-40" /><Skeleton className="h-2 w-full" /><Skeleton className="h-4 w-72" /></div>
        ) : (
          <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            <div className="flex-1">
              <div className="flex items-center justify-between text-sm">
                <span className="font-medium">{t("brandBrain.completeness")}</span>
                <span className="font-heading text-lg font-semibold" data-testid="brand-brain-completeness">{t("common.percent", { value: data.completeness_score })}</span>
              </div>
              <Progress value={data.completeness_score} className="mt-2 h-2" />
            </div>
            <div className={cn("flex items-start gap-3 rounded-lg border px-3 py-2 lg:max-w-md", readiness?.ready_for_agents ? "border-emerald-200 bg-emerald-50 text-emerald-900" : "border-amber-200 bg-amber-50 text-amber-950")} data-testid="brand-brain-readiness-status">
              {readiness?.ready_for_agents ? <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0" /> : <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />}
              <div className="text-sm">
                <div className="font-medium">{readiness?.ready_for_agents ? t("brandBrain.readyForAgents") : t("brandBrain.notReadyForAgents", { count: readiness?.missing_required?.length || 0 })}</div>
                <div className="text-xs opacity-90">{readiness?.ready_for_agents ? t("brandBrain.readyHint") : t("brandBrain.notReadyHint")}</div>
                <Link to="/app/agents" className="og-focus mt-2 inline-block rounded text-xs font-medium underline" data-testid="brand-brain-open-agents">{t("brandBrain.openAgents")}</Link>
                {!readiness?.ready_for_agents && (
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {readiness?.missing_required?.map((f) => (
                      <button key={f} type="button" onClick={() => selectSection(FIELD_SECTION[f] || "identity")} className="og-focus rounded-md border border-amber-300 bg-white/70 px-2 py-0.5 text-xs font-medium hover:bg-white" data-testid={`brand-brain-missing-${f}`}>{t(`brandBrain.fields.${f}`)}</button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </section>

      {!canEdit && <Alert className="border-border bg-muted"><AlertDescription>{t("errors.insufficient_role")}</AlertDescription></Alert>}

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-[292px_minmax(0,1fr)]">
        {/* Section navigation */}
        <nav aria-label={t("brandBrain.title")} className="og-scroll -mx-4 flex gap-1 overflow-x-auto px-4 pb-1 lg:sticky lg:top-20 lg:mx-0 lg:block lg:self-start lg:space-y-0.5 lg:overflow-visible lg:px-0" data-testid="brand-brain-section-nav">
          {SECTION_DEFS.map(({ key, icon: Icon }, i) => {
            const pct = sectionPct[key] ?? 0;
            const isActive = key === section;
            return (
              <button
                key={key}
                type="button"
                onClick={() => selectSection(key)}
                data-testid={`brand-brain-section-${key}`}
                aria-current={isActive ? "page" : undefined}
                className={cn("og-focus flex shrink-0 items-center gap-2.5 rounded-lg border px-3 py-2 text-left text-sm transition-colors duration-150 lg:w-full", isActive ? "border-primary/30 bg-accent text-foreground" : "border-transparent text-muted-foreground hover:bg-muted hover:text-foreground")}
              >
                <span className={cn("flex h-6 w-6 shrink-0 items-center justify-center rounded-md text-[11px] font-semibold", isActive ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground")}>{i + 1}</span>
                <Icon className="hidden h-4 w-4 shrink-0 lg:block" aria-hidden="true" />
                <span className="whitespace-nowrap font-medium lg:flex-1 lg:truncate">{t(`brandBrain.sections.${key}`)}</span>
                <span className={cn("ml-1 text-[11px] tabular-nums", pct === 100 ? "text-emerald-700" : "text-muted-foreground")} data-testid={`brand-brain-section-pct-${key}`}>{pct}%</span>
              </button>
            );
          })}
        </nav>

        {/* Section form */}
        <div className="space-y-4">
          <section className="og-card" data-testid={`brand-brain-form-${section}`}>
            <div className="flex flex-col gap-2 border-b border-border px-5 py-4 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <h2 className="font-heading text-base font-semibold">{t(`brandBrain.sections.${section}`)}</h2>
                <p className="text-sm text-muted-foreground">{t(`brandBrain.sectionHints.${section}`)}</p>
              </div>
              <Badge variant="outline" className={cn("shrink-0", (sectionPct[section] ?? 0) === 100 ? "border-emerald-200 bg-emerald-50 text-emerald-900" : "border-border bg-muted text-muted-foreground")}>{t("brandBrain.sectionComplete", { percent: sectionPct[section] ?? 0 })}</Badge>
            </div>
            <div className="p-5">
              {isLoading || !data ? (
                <div className="space-y-4"><Skeleton className="h-10 w-full" /><Skeleton className="h-10 w-full" /><Skeleton className="h-24 w-full" /></div>
              ) : (
                <fieldset disabled={!canEdit || save.isPending} className="min-w-0">
                  {active && <SectionForm section={section} draft={draft} setField={setField} setNested={setNested} />}
                </fieldset>
              )}
            </div>
            {canEdit && (
              <div className="flex flex-col-reverse gap-3 border-t border-border px-5 py-4 sm:flex-row sm:items-center sm:justify-between">
                <div className="text-xs text-muted-foreground" data-testid="brand-brain-dirty-indicator">{dirty ? t("brandBrain.unsaved") : data?.updated_at ? `${t("common.updated")} · ${formatDateTime(data.updated_at)}` : ""}</div>
                <div className="flex items-center gap-2">
                  <Button variant="ghost" onClick={() => setEdits({})} disabled={!dirty || save.isPending} data-testid="brand-brain-discard">{t("brandBrain.discard")}</Button>
                  <Button onClick={() => save.mutate()} disabled={!dirty || save.isPending} data-testid="brand-brain-save">{save.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}{t("brandBrain.saveSection")}</Button>
                </div>
              </div>
            )}
          </section>

          {/* History */}
          <Collapsible className="og-card" data-testid="brand-brain-history">
            <CollapsibleTrigger asChild>
              <button type="button" className="og-focus flex w-full items-center justify-between rounded-xl px-5 py-4 text-left transition-colors duration-150 hover:bg-muted/40" data-testid="brand-brain-history-toggle">
                <div className="flex items-center gap-2">
                  <History className="h-4 w-4 text-muted-foreground" />
                  <span className="font-heading text-sm font-semibold">{t("brandBrain.history")}</span>
                  <span className="text-xs text-muted-foreground">({revisions?.length ?? 0})</span>
                </div>
                <span className="text-xs text-muted-foreground">{t("brandBrain.historyHint")}</span>
              </button>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <div className="border-t border-border px-5 py-4">
                {!revisions?.length ? (
                  <p className="text-sm text-muted-foreground">{t("brandBrain.historyEmpty")}</p>
                ) : (
                  <ol className="space-y-3">
                    {revisions.map((rev) => (
                      <li key={rev.id} className="flex flex-col gap-1 rounded-lg border border-border bg-background px-3 py-2 text-sm sm:flex-row sm:items-start sm:justify-between" data-testid="brand-brain-revision">
                        <div className="min-w-0">
                          <div className="text-xs text-muted-foreground">{formatDateTime(rev.created_at)}{rev.user_name ? ` · ${t("brandBrain.by", { name: rev.user_name })}` : ""}{rev.source && rev.source !== "user" ? ` · ${rev.source}` : ""}</div>
                          <div className="mt-1 flex flex-wrap gap-1">
                            {(rev.changed_fields || []).map((f) => <span key={f} className="rounded-md border border-border bg-muted px-1.5 py-0.5 text-[11px]">{t(`brandBrain.fields.${f}`, { defaultValue: f })}</span>)}
                          </div>
                        </div>
                        {rev.completeness_before !== null && rev.completeness_after !== null && (
                          <div className="shrink-0 text-xs tabular-nums text-muted-foreground">{rev.completeness_before}% → <span className="font-medium text-foreground">{rev.completeness_after}%</span></div>
                        )}
                      </li>
                    ))}
                  </ol>
                )}
              </div>
            </CollapsibleContent>
          </Collapsible>
        </div>
      </div>
    </div>
  );
}
